import { UI_DEFAULTS } from '../locale/labels';
const DEFAULT_ACCENT = UI_DEFAULTS.theme.bubbleUserBg;
const DEFAULT_BG = '#ffffff';
const DEFAULT_TEXT = '#0f172a';
const DEFAULT_TEXT_SECONDARY = '#475569';
const DEFAULT_BORDER = UI_DEFAULTS.theme.surfaceBorder;
const DEFAULT_ACTIVE_BG = UI_DEFAULTS.theme.surfaceBgActive;
const DEFAULT_BORDER_RADIUS = 8;
const NO_SHADOW = 'none';

function readThemeText(themeTokens = {}, key, fallback = '') {
  return String(themeTokens?.[key] ?? fallback ?? '').trim();
}

function pickThemeText(themeTokens = {}, keys = [], fallback = '') {
  return keys
    .map((key) => readThemeText(themeTokens, key))
    .find(Boolean) || String(fallback || '').trim();
}

/**
 * Build the shared Ant Design theme contract from FLARE theme tokens.
 *
 * @param {object} themeTokens - FLARE presentation theme tokens.
 * Builds AntD ConfigProvider tokens from the active presentation palette.
 */
export function buildAntdThemeConfig(themeTokens = {}) {
  const accent = pickThemeText(themeTokens, ['bubbleUserBg', 'surfaceBorderActive'], DEFAULT_ACCENT);
  const accentHover = pickThemeText(themeTokens, ['surfaceBorderActive', 'bubbleUserBg'], accent);
  const appBg = pickThemeText(themeTokens, ['appBg', 'panelBg'], DEFAULT_BG);
  const panelBg = pickThemeText(themeTokens, ['panelBg', 'surfaceBg'], appBg);
  const surfaceBg = pickThemeText(themeTokens, ['surfaceBg', 'composerBoxBg', 'panelBg'], panelBg);
  const activeBg = pickThemeText(themeTokens, ['surfaceBgActive', 'scenarioCardBgActive'], DEFAULT_ACTIVE_BG);
  const border = pickThemeText(themeTokens, ['surfaceBorder', 'divider'], DEFAULT_BORDER);
  const divider = pickThemeText(themeTokens, ['divider', 'surfaceBorder'], border);
  const textPrimary = pickThemeText(themeTokens, ['textPrimary'], DEFAULT_TEXT);
  const textSecondary = pickThemeText(themeTokens, ['textSecondary'], DEFAULT_TEXT_SECONDARY);
  const textMuted = pickThemeText(themeTokens, ['textMuted', 'textSecondary'], textSecondary);
  const focusOutline = readThemeText(themeTokens, 'focusOutline', NO_SHADOW);
  const fontFamily = readThemeText(themeTokens, 'fontFamilyBody');

  return {
    token: {
      borderRadius: DEFAULT_BORDER_RADIUS,
      colorBgBase: appBg,
      colorBgContainer: surfaceBg,
      colorBgElevated: panelBg,
      colorBgLayout: appBg,
      colorBorder: border,
      colorBorderSecondary: divider,
      colorFillSecondary: activeBg,
      colorFillTertiary: activeBg,
      colorInfo: accent,
      colorLink: accent,
      colorLinkHover: accentHover,
      colorPrimary: accent,
      colorPrimaryActive: accentHover,
      colorPrimaryHover: accentHover,
      colorText: textPrimary,
      colorTextSecondary: textSecondary,
      colorTextTertiary: textMuted,
      ...(fontFamily ? { fontFamily } : {}),
    },
    components: {
      Alert: {
        colorInfo: accent,
      },
      Button: {
        borderRadius: DEFAULT_BORDER_RADIUS,
        colorPrimary: accent,
        colorPrimaryActive: accentHover,
        colorPrimaryHover: accentHover,
        primaryShadow: NO_SHADOW,
      },
      Card: {
        colorBgContainer: surfaceBg,
        colorBorderSecondary: border,
      },
      Checkbox: {
        colorPrimary: accent,
        colorPrimaryHover: accentHover,
      },
      Drawer: {
        colorBgElevated: panelBg,
      },
      Input: {
        activeBorderColor: accentHover,
        activeShadow: focusOutline,
        hoverBorderColor: accentHover,
      },
      Modal: {
        contentBg: panelBg,
        headerBg: panelBg,
      },
      Popover: {
        colorBgElevated: panelBg,
      },
      Progress: {
        defaultColor: accent,
        remainingColor: divider,
      },
      Radio: {
        colorPrimary: accent,
        colorPrimaryHover: accentHover,
      },
      Segmented: {
        itemSelectedBg: surfaceBg,
        itemSelectedColor: textPrimary,
        trackBg: activeBg,
      },
      Select: {
        activeBorderColor: accentHover,
        optionSelectedBg: activeBg,
        optionSelectedColor: textPrimary,
      },
      Slider: {
        handleActiveColor: accentHover,
        handleColor: accent,
        trackBg: accent,
        trackHoverBg: accentHover,
      },
      Switch: {
        colorPrimary: accent,
        colorPrimaryHover: accentHover,
      },
      Tabs: {
        inkBarColor: accent,
        itemActiveColor: accent,
        itemHoverColor: accentHover,
        itemSelectedColor: accent,
      },
      Tag: {
        defaultBg: activeBg,
        defaultColor: textSecondary,
      },
    },
  };
}
